import { useState } from "react";
import { useThreadRuntime, useMessage } from "@assistant-ui/react";
import { CheckOutlined } from "@ant-design/icons";
import CopyChatMsg from "../../../../assets/icons/CopyChatMsg";
import ThumbUpChatMsg from "../../../../assets/icons/ThumbUpChatMsg";
import ThumbDownChatMsg from "../../../../assets/icons/ThumbDownChatMsg";
import { copyToClipboard } from "helpers/utility/utilities";
import { COLLAPSIBLE_REGEX } from "../constants";
import { useToolStatus } from "../../VLChat/VLChatWrapper";
import styles from "./MessageActions.module.scss";

/**
 * Strip collapsible markers from text for clean copying.
 * Removes the entire collapsible section (marker + content).
 */
const stripCollapsibleMarkers = (text: string): string => {
  return text.replace(COLLAPSIBLE_REGEX, "").trim();
};

export default function MessageActions() {
  const [isCopied, setIsCopied] = useState(false);
  const threadRuntime = useThreadRuntime();
  const message = useMessage();
  const { showToolStatus, isThinking } = useToolStatus();
  const isMessageComplete = !(message.isLast && (showToolStatus || isThinking));

  // Extract text content from message for copying
  const textContent = (
    message.content as Array<{ type: string; text?: string }>
  )
    .filter((part) => part.type === "text" && part.text)
    .map((part) => part.text as string)
    .join("\n");

  const handleCopy = async () => {
    if (!textContent) return;
    try {
      // Strip collapsible markers before copying
      const cleanText = stripCollapsibleMarkers(textContent);
      await copyToClipboard(cleanText);
      setIsCopied(true);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleAnimationEnd = () => {
    setIsCopied(false); // Reset to copy icon when animation completes
  };

  const handleFeedback = (positive: boolean) => () => {
    // Match old CopilotKit behavior: inject feedback message into composer
    const prefix = positive ? "+feedback:" : "-feedback:";
    const placeholder = positive
      ? "What made you smile?"
      : "Where's the bug hiding?";

    // Set thread composer text (not edit composer)
    threadRuntime.composer.setText(`${prefix} ${placeholder} `);
  };

  // Don't render actions at all while message is being typed
  if (!isMessageComplete) {
    return null;
  }

  return (
    <div className={styles.actions}>
      <button
        className={styles.actionButton}
        onClick={handleCopy}
        title={isCopied ? "Copied!" : "Copy"}
        type="button"
      >
        {isCopied ? (
          <CheckOutlined
            className={styles.copied}
            onAnimationEnd={handleAnimationEnd}
          />
        ) : (
          <CopyChatMsg />
        )}
      </button>
      <button
        className={styles.actionButton}
        onClick={handleFeedback(true)}
        title="Good response"
        type="button"
      >
        <ThumbUpChatMsg />
      </button>
      <button
        className={styles.actionButton}
        onClick={handleFeedback(false)}
        title="Poor response"
        type="button"
      >
        <ThumbDownChatMsg />
      </button>
    </div>
  );
}
